import { slugs } from '@/constants/slugs'
import { GuildPermissions } from '@/services/GuildUserService'
import { inTheaters, isStreaming } from '@/services/PhaseManagerService'
import { isSellingTickets } from '@/services/ProjectPhaseService'
import { Episode, Project, ResumeWatchingData, VideoMetaData } from '@/services/ProjectsService'
import { TicketsStatusReference } from '@/types/codegen-federation'
import {
  isAClip,
  isALivestream,
  isAPitch,
  isAPreview,
  isASneakPeek,
  isATeaser,
  isATorch,
  Media,
} from '@/utils/EpisodeUtil'
import { TranslateFunction } from '@/utils/translate/translate-client'

const slugsToHideEpisodeNumbers = [slugs.lightwise, slugs.dryBar, slugs.tuttleTwinsMinisodes, slugs.theGermanKing]

// Helpers: get episodes from normalized project.seasons (which comes from project.title.seasons)
const getFirstSeasonEpisodesPreferred = (projectData: Project): Episode[] => {
  const legacyEpisodes: Episode[] = projectData?.seasons?.[0]?.episodes ?? []

  const titleSeasonsEdges = projectData?.title?.seasons?.edges ?? []
  const firstTitleSeasonNode = titleSeasonsEdges?.[0]?.node
  const titleEpisodeNodes = firstTitleSeasonNode?.episodes?.edges?.map((e) => e?.node).filter(Boolean) as
    | { slug?: string | null; name?: string | null }[]
    | undefined

  if (titleEpisodeNodes && titleEpisodeNodes.length && legacyEpisodes.length) {
    const ordered: Episode[] = []
    for (const node of titleEpisodeNodes) {
      const matchBySlug = legacyEpisodes.find((ep) => ep?.slug && node.slug && ep.slug === node.slug)
      if (matchBySlug) {
        ordered.push(matchBySlug)
        continue
      }
      const nodeName = (node.name ?? '').toLowerCase()
      if (!nodeName) continue
      const matchByName = legacyEpisodes.find((ep) => ep?.name && ep.name.toLowerCase() === nodeName)
      if (matchByName) ordered.push(matchByName)
    }
    if (ordered.length) return ordered
  }

  return legacyEpisodes
}

const getAllEpisodesPreferred = (projectData: Project): Episode[] => {
  const legacyEpisodes: Episode[] = (projectData?.seasons ?? []).flatMap((s) => s?.episodes ?? [])
  if (!legacyEpisodes.length) return []

  const titleSeasonsEdges = projectData?.title?.seasons?.edges ?? []
  const titleEpisodeNodes = titleSeasonsEdges
    .flatMap((s) => s?.node?.episodes?.edges ?? [])
    .map((e) => e?.node)
    .filter(Boolean) as { slug?: string | null; name?: string | null }[]

  const matchNode = (node: { slug?: string | null; name?: string | null }): Episode | undefined => {
    const bySlug = legacyEpisodes.find((ep) => ep?.slug && node.slug && ep.slug === node.slug)
    if (bySlug) return bySlug
    const nodeName = (node.name ?? '').toLowerCase()
    if (!nodeName) return undefined
    return legacyEpisodes.find((ep) => ep?.name && ep.name.toLowerCase() === nodeName)
  }

  if (titleEpisodeNodes.length) {
    const ordered = titleEpisodeNodes.map((n) => matchNode(n)).filter(Boolean) as Episode[]
    if (ordered.length) return ordered
  }

  return legacyEpisodes
}

type CtaValues = {
  primaryCtaPath: string
  primaryCtaText: string
  primaryCtaIcon: string
  secondaryCtaPath: string
  secondaryCtaText: string
  secondaryCtaIcon: string
}

type WatchProjectCTAResult = {
  primaryCtaPath: string | null
  primaryCtaText: string | null
  primaryCtaIcon: string | null
  secondaryCtaPath: string | null
  secondaryCtaText: string | null
  secondaryCtaIcon: string | null
  trailer: ((VideoMetaData | Episode | Media) & { isBonus?: boolean }) | null
  primaryCtaGuid: string | null
}

type BaseCtaParams = {
  projectData: Project
  t: TranslateFunction
  defaultCtas: CtaValues
  sneakPeek?: ((VideoMetaData | Episode | Media) & { isBonus?: boolean }) | null
  trailer?: ((VideoMetaData | Episode | Media) & { isBonus?: boolean }) | null
}

type UserParams = {
  isGuildMember: boolean
  isGuildMemberContent: boolean
  guildPermissions: GuildPermissions
  watchableReasons?: string[]
  guildCodes?: string[]
  areGuildTicketsAvailable?: boolean
  activeSweepstake?: boolean
}

const getProjectStatus = (
  projectData: Project,
  movie?: Episode | Media,
  ticketStatus?: TicketsStatusReference | null,
) => {
  const now = new Date()
  const isMovie = projectData?.projectType === 'movie'
  const currentlyInTheaters = inTheaters(projectData?.primaryFlowPhases ?? [])
  const sellingTickets = isSellingTickets(ticketStatus)
  const currentlyStreaming = isStreaming(projectData?.primaryFlowPhases ?? [])
  const isMovieAvailableToStream =
    (isMovie && movie?.earlyAccessDate && new Date(movie.earlyAccessDate) < now) ||
    (isMovie && movie?.publiclyAvailableDate && new Date(movie.publiclyAvailableDate) < now)
  const isSeriesAvailableToStream = projectData?.projectType === 'series' && hasAnyEpisodeAvailable(projectData)

  return {
    isMovie,
    currentlyInTheaters,
    sellingTickets,
    currentlyStreaming: Boolean(currentlyStreaming || isMovieAvailableToStream || isSeriesAvailableToStream),
  }
}

const getWatchableContent = (projectData: Project, bonusVideos?: VideoMetaData[], movieSpecialMedias?: Media[]) => {
  return {
    trailer: findTrailer(projectData, bonusVideos, movieSpecialMedias),
    sneakPeek: findSneakPeek(projectData, bonusVideos, movieSpecialMedias),
    watchableEpisode: findFirstWatchableEpisode(projectData, bonusVideos),
    movie: findMovieEpisode(projectData, movieSpecialMedias),
    special: findSpecialEpisode(projectData, movieSpecialMedias),
  }
}

const getEpisodeInfoFromResumeWatching = (resumeWatchingData?: ResumeWatchingData) => {
  if (resumeWatchingData?.content?.__typename !== 'ContentEpisode') {
    return { seasonNumber: undefined, episodeNumber: undefined }
  }

  return {
    seasonNumber: resumeWatchingData.content.season?.seasonNumber ?? undefined,
    episodeNumber: resumeWatchingData.content.episodeNumber ?? undefined,
  }
}

const getContinueWatchingInfo = (projectData: Project, resumeWatchingData?: ResumeWatchingData) => {
  const lastWatchedGuid = resumeWatchingData?.watchableGuid ?? undefined
  const episodeInfo = getEpisodeInfoFromResumeWatching(resumeWatchingData)
  let continueWatchingSeasonNumber = episodeInfo.seasonNumber
  let continueWatchingEpisodeNumber = episodeInfo.episodeNumber

  if ((continueWatchingSeasonNumber == null || continueWatchingEpisodeNumber == null) && lastWatchedGuid) {
    const allEpisodes = getAllEpisodesPreferred(projectData)
    if (allEpisodes.length) {
      const matched = allEpisodes.find((ep) => ep?.guid === lastWatchedGuid)
      if (matched) {
        if (continueWatchingSeasonNumber == null && typeof matched.seasonNumber === 'number') {
          continueWatchingSeasonNumber = matched.seasonNumber
        }
        if (continueWatchingEpisodeNumber == null && typeof matched.episodeNumber === 'number') {
          continueWatchingEpisodeNumber = matched.episodeNumber
        }
      }
    }
  }

  return {
    lastWatched: lastWatchedGuid,
    continueWatchingSeasonNumber,
    continueWatchingEpisodeNumber,
    hideEpisodeNumbers: Boolean(projectData?.slug && slugsToHideEpisodeNumbers.includes(projectData.slug)),
  }
}

const createDefaultCtas = (ctaPath?: string, ctaText?: string): CtaValues => {
  return {
    primaryCtaPath: ctaPath ?? '',
    primaryCtaText: ctaText ?? '',
    primaryCtaIcon: 'play',
    secondaryCtaPath: '',
    secondaryCtaText: '',
    secondaryCtaIcon: '',
  }
}

const formatCtaResult = (
  defaultCtas: CtaValues,
  trailer: ((VideoMetaData | Episode | Media) & { isBonus?: boolean }) | null,
  watchableContent: ReturnType<typeof getWatchableContent>,
): WatchProjectCTAResult => {
  // First try to extract guid from the path (for direct episode links)
  let primaryCtaGuid = extractGuidFromPath(defaultCtas.primaryCtaPath)

  // If no guid in path (e.g., /guild/join or /tickets), use the main movie/special guid
  if (!primaryCtaGuid) {
    const mainContent = watchableContent.movie || watchableContent.special
    primaryCtaGuid = mainContent?.guid ?? null
  }

  return {
    primaryCtaPath: defaultCtas.primaryCtaPath === '' ? null : defaultCtas.primaryCtaPath || null,
    primaryCtaText: defaultCtas.primaryCtaText === '' ? null : defaultCtas.primaryCtaText || null,
    primaryCtaIcon: defaultCtas.primaryCtaIcon === '' ? null : defaultCtas.primaryCtaIcon || null,
    secondaryCtaPath: defaultCtas.secondaryCtaPath === '' ? null : defaultCtas.secondaryCtaPath || null,
    secondaryCtaText: defaultCtas.secondaryCtaText === '' ? null : defaultCtas.secondaryCtaText || null,
    secondaryCtaIcon: defaultCtas.secondaryCtaIcon === '' ? null : defaultCtas.secondaryCtaIcon || null,
    trailer,
    primaryCtaGuid,
  }
}

const extractGuidFromPath = (path: string): string | null => {
  if (!path) return null

  const episodeMatch = path.match(/\/episode\/([^/]+)$/)
  if (episodeMatch && episodeMatch[1]) return episodeMatch[1]
  const bonusMatch = path.match(/\/videos\/bonus\/([^/]+)$/)
  if (bonusMatch && bonusMatch[1]) return bonusMatch[1]
  return null
}

// Handle movie that is currently streaming
const handleStreamingMovie = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  lastWatched,
  movie,
  isGuildMemberContent,
  isGuildMember,
  watchableReasons,
}: BaseCtaParams & {
  lastWatched?: string
  movie?: Episode | Media
  isGuildMemberContent: boolean
  isGuildMember: boolean
  watchableReasons?: string[]
}): CtaValues => {
  if (isGuildMemberContent && !(isGuildMember || (!isGuildMember && watchableReasons?.length))) {
    defaultCtas.primaryCtaText = t('joinToWatch', 'Join To Watch')
    defaultCtas.primaryCtaPath = `/guild/join/${projectData.slug}`
    defaultCtas.primaryCtaIcon = 'guild'
    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
    })

    return defaultCtas
  }
  if (lastWatched) {
    defaultCtas.primaryCtaText = t('resume', 'Resume')
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${lastWatched}`
    defaultCtas.primaryCtaIcon = 'play'
  } else {
    defaultCtas.primaryCtaText = t('watchNow', 'Watch Now')
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${movie?.guid}`
    defaultCtas.primaryCtaIcon = 'play'
  }

  getPreviewContentCta({
    isPrimary: false,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    defaultTrailer: true,
    isGuildMember,
  })

  return defaultCtas
}

// Handle movie that is in theaters
const handleInTheatersMovie = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  isGuildMember,
  guildPermissions,
  areGuildTicketsAvailable,
  guildCodes,
}: BaseCtaParams & {
  isGuildMember: boolean
  guildPermissions: GuildPermissions
  areGuildTicketsAvailable?: boolean
  guildCodes?: string[]
}): CtaValues => {
  if (isGuildMember && guildPermissions.hasFreeTickets && areGuildTicketsAvailable && guildCodes?.length) {
    defaultCtas.primaryCtaText = t('redeemGuildTickets', 'Redeem Guild Tickets')
  } else {
    defaultCtas.primaryCtaText = t('getShowtimes', 'Get Showtimes')
  }

  defaultCtas.primaryCtaPath = `/tickets/${projectData.slug}`
  defaultCtas.primaryCtaIcon = 'ticket'

  getPreviewContentCta({
    isPrimary: false,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    isGuildMember,
  })

  return defaultCtas
}

// Handle movie when tickets are being sold (but not in theaters yet)
const handleSellingTicketsMovie = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  isGuildMember,
  areGuildTicketsAvailable,
  guildCodes,
}: BaseCtaParams & {
  isGuildMember: boolean
  activeSweepstake?: boolean
  areGuildTicketsAvailable?: boolean
  guildCodes?: string[]
  sellingTickets?: boolean
}): CtaValues => {
  const basePath = `/tickets/${projectData.slug}`

  if (isGuildMember && areGuildTicketsAvailable && guildCodes?.length) {
    defaultCtas.primaryCtaText = t('redeemGuildTickets', 'Redeem Guild Tickets')
    defaultCtas.primaryCtaIcon = 'guild'
  } else {
    defaultCtas.primaryCtaText = t('getShowtimes', 'Get Showtimes')
    defaultCtas.primaryCtaIcon = 'ticket'
  }

  defaultCtas.primaryCtaPath = basePath

  getPreviewContentCta({
    isPrimary: false,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    isGuildMember,
  })

  return defaultCtas
}

// Handle movie that has giveaway
const handleGiveawayTicketsMovie = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  isGuildMember,
  areGuildTicketsAvailable,
  guildCodes,
}: BaseCtaParams & {
  isGuildMember: boolean
  activeSweepstake?: boolean
  areGuildTicketsAvailable?: boolean
  guildCodes?: string[]
  sellingTickets?: boolean
}): CtaValues => {
  const basePath = `/giveaway/${projectData.slug}`

  if (isGuildMember && areGuildTicketsAvailable && guildCodes?.length) {
    defaultCtas.primaryCtaText = t('redeemGuildTickets', 'Redeem Guild Tickets')
    defaultCtas.primaryCtaIcon = 'guild'
  } else {
    defaultCtas.primaryCtaText = t('preOrderTickets', 'Pre-Order Tickets')
    defaultCtas.primaryCtaIcon = 'ticket'
  }

  defaultCtas.primaryCtaPath = basePath

  getPreviewContentCta({
    isPrimary: false,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    isGuildMember,
  })

  return defaultCtas
}

const getSeriesResumeText = ({
  t,
  continueWatchingSeasonNumber,
  continueWatchingEpisodeNumber,
  hideEpisodeNumbers,
}: {
  t: TranslateFunction
  continueWatchingSeasonNumber?: number
  continueWatchingEpisodeNumber?: number
  hideEpisodeNumbers: boolean
}): string => {
  return continueWatchingSeasonNumber && continueWatchingEpisodeNumber && !hideEpisodeNumbers
    ? t('resumeEpisodeSeason', `Resume S{{ seasonNumber }} E{{ episodeNumber}}`, {
        seasonNumber: continueWatchingSeasonNumber,
        episodeNumber: continueWatchingEpisodeNumber,
      })
    : t('resume', 'Resume')
}

const hasSeasonAndEpisodeNumbers = (
  ep: Episode | VideoMetaData | null | undefined,
): ep is Episode & { seasonNumber: number; episodeNumber: number } => {
  return Boolean(
    ep &&
      'seasonNumber' in ep &&
      'episodeNumber' in ep &&
      typeof (ep as Episode).seasonNumber === 'number' &&
      typeof (ep as Episode).episodeNumber === 'number',
  )
}

const getSeriesEpisodeText = ({
  t,
  watchableEpisode,
  hideEpisodeNumbers,
}: {
  t: TranslateFunction
  watchableEpisode?: Episode | VideoMetaData | null
  hideEpisodeNumbers: boolean
}): string => {
  if (hasSeasonAndEpisodeNumbers(watchableEpisode) && !hideEpisodeNumbers) {
    return t('watchEpisodeSeason', `Watch S{{ seasonNumber }} E{{ episodeNumber}}`, {
      seasonNumber: watchableEpisode.seasonNumber,
      episodeNumber: watchableEpisode.episodeNumber,
    })
  }

  if (isATorch(watchableEpisode)) {
    return t('watchTorch', 'Watch Torch')
  }

  return t('watchNow', 'Watch Now')
}

// Handle streaming series with Guild Member content
const handleGuildMemberStreamingSeries = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  lastWatched,
  isGuildMember,
  watchableEpisode,
  hideEpisodeNumbers,
  continueWatchingSeasonNumber,
  continueWatchingEpisodeNumber,
}: BaseCtaParams & {
  lastWatched?: string
  isGuildMember: boolean
  watchableEpisode?: Episode | VideoMetaData | null
  hideEpisodeNumbers: boolean
  continueWatchingSeasonNumber?: number
  continueWatchingEpisodeNumber?: number
}): CtaValues => {
  if (isGuildMember) {
    if (lastWatched) {
      defaultCtas.primaryCtaText = getSeriesResumeText({
        t,
        continueWatchingSeasonNumber,
        continueWatchingEpisodeNumber,
        hideEpisodeNumbers,
      })
      defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${lastWatched}`
      defaultCtas.primaryCtaIcon = 'play'

      getPreviewContentCta({
        isPrimary: false,
        projectData,
        t,
        defaultCtas,
        sneakPeek,
        trailer,
        defaultTrailer: true,
        isGuildMember,
      })
    } else if (watchableEpisode) {
      defaultCtas.primaryCtaText = getSeriesEpisodeText({
        t,
        watchableEpisode,
        hideEpisodeNumbers,
      })
      defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${watchableEpisode?.guid}`
      defaultCtas.primaryCtaIcon = 'play'

      getPreviewContentCta({
        isPrimary: false,
        projectData,
        t,
        defaultCtas,
        sneakPeek,
        trailer,
        defaultTrailer: true,
        isGuildMember,
      })
    } else {
      defaultCtas.primaryCtaText = ''
      defaultCtas.primaryCtaPath = ''
      defaultCtas.primaryCtaIcon = ''
    }
  } else {
    handleNonGuildMemberStreamingSeries({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      lastWatched,
      watchableEpisode,
      hideEpisodeNumbers,
      continueWatchingSeasonNumber,
      continueWatchingEpisodeNumber,
      isGuildMember,
    })
  }

  return defaultCtas
}

// Handle streaming series for non-Guild members
const handleNonGuildMemberStreamingSeries = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  lastWatched,
  watchableEpisode,
  hideEpisodeNumbers,
  continueWatchingSeasonNumber,
  continueWatchingEpisodeNumber,
  isGuildMember,
}: BaseCtaParams & {
  lastWatched?: string
  watchableEpisode?: Episode | VideoMetaData | null
  hideEpisodeNumbers: boolean
  continueWatchingSeasonNumber?: number
  continueWatchingEpisodeNumber?: number
  isGuildMember?: boolean
}): CtaValues => {
  if (lastWatched) {
    defaultCtas.primaryCtaText = getSeriesResumeText({
      t,
      continueWatchingSeasonNumber,
      continueWatchingEpisodeNumber,
      hideEpisodeNumbers,
    })
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${lastWatched}`
    defaultCtas.primaryCtaIcon = 'play'

    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      defaultTrailer: true,
      isGuildMember,
    })
  } else if (watchableEpisode) {
    const isPubliclyAvailable =
      'publiclyAvailableDate' in watchableEpisode &&
      watchableEpisode?.publiclyAvailableDate &&
      new Date(watchableEpisode.publiclyAvailableDate) < new Date()

    if (isPubliclyAvailable) {
      defaultCtas.primaryCtaText = getSeriesEpisodeText({
        t,
        watchableEpisode,
        hideEpisodeNumbers,
      })
      defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${watchableEpisode?.guid}`
      defaultCtas.primaryCtaIcon = 'play'
    } else {
      defaultCtas.primaryCtaText = t('joinToWatch', 'Join To Watch')
      defaultCtas.primaryCtaPath = `/guild/join/${projectData.slug}`
      defaultCtas.primaryCtaIcon = 'guild'
    }

    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
    })
  } else {
    defaultCtas.primaryCtaText = ''
    defaultCtas.primaryCtaPath = ''
    defaultCtas.primaryCtaIcon = ''
  }

  return defaultCtas
}

// Handle publicly available streaming series
const handlePublicStreamingSeries = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  lastWatched,
  watchableEpisode,
  hideEpisodeNumbers,
  continueWatchingSeasonNumber,
  continueWatchingEpisodeNumber,
  isGuildMember,
}: BaseCtaParams & {
  lastWatched?: string
  watchableEpisode?: Episode | VideoMetaData | null
  hideEpisodeNumbers: boolean
  continueWatchingSeasonNumber?: number
  continueWatchingEpisodeNumber?: number
  isGuildMember?: boolean
}): CtaValues => {
  if (lastWatched) {
    defaultCtas.primaryCtaText = getSeriesResumeText({
      t,
      continueWatchingSeasonNumber,
      continueWatchingEpisodeNumber,
      hideEpisodeNumbers,
    })
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${lastWatched}`
    defaultCtas.primaryCtaIcon = 'play'

    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      defaultTrailer: true,
      isGuildMember,
    })
  } else if (watchableEpisode) {
    defaultCtas.primaryCtaText = getSeriesEpisodeText({
      t,
      watchableEpisode,
      hideEpisodeNumbers,
    })
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${watchableEpisode?.guid}`
    defaultCtas.primaryCtaIcon = 'play'

    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
    })
  } else {
    defaultCtas.primaryCtaText = ''
    defaultCtas.primaryCtaPath = ''
    defaultCtas.primaryCtaIcon = ''
  }

  return defaultCtas
}

const handleSeriesContent = (
  projectData: Project,
  t: TranslateFunction,
  defaultCtas: CtaValues,
  {
    sneakPeek,
    trailer,
    watchableEpisode,
  }: Pick<ReturnType<typeof getWatchableContent>, 'sneakPeek' | 'trailer' | 'watchableEpisode'>,
  continueWatchingInfo: ReturnType<typeof getContinueWatchingInfo>,
  { isGuildMember, isGuildMemberContent }: Pick<UserParams, 'isGuildMember' | 'isGuildMemberContent'>,
  isStreaming: boolean,
) => {
  // Non-streaming series shows preview content
  if (!isStreaming) {
    return getPreviewContentCta({
      isPrimary: true,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      defaultTrailer: true,
      isGuildMember,
    })
  }

  // Handle streaming series based on content type
  if (isGuildMemberContent) {
    return handleGuildMemberStreamingSeries({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      lastWatched: continueWatchingInfo.lastWatched,
      isGuildMember,
      watchableEpisode,
      hideEpisodeNumbers: continueWatchingInfo.hideEpisodeNumbers,
      continueWatchingSeasonNumber: continueWatchingInfo.continueWatchingSeasonNumber,
      continueWatchingEpisodeNumber: continueWatchingInfo.continueWatchingEpisodeNumber,
    })
  }

  return handlePublicStreamingSeries({
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    lastWatched: continueWatchingInfo.lastWatched,
    watchableEpisode,
    hideEpisodeNumbers: continueWatchingInfo.hideEpisodeNumbers,
    continueWatchingSeasonNumber: continueWatchingInfo.continueWatchingSeasonNumber,
    continueWatchingEpisodeNumber: continueWatchingInfo.continueWatchingEpisodeNumber,
    isGuildMember,
  })
}

// Handle movie based on phase status
const handleMovieContent = (
  projectData: Project,
  t: TranslateFunction,
  defaultCtas: CtaValues,
  status: ReturnType<typeof getProjectStatus>,
  watchableContent: ReturnType<typeof getWatchableContent>,
  continueWatchingInfo: ReturnType<typeof getContinueWatchingInfo>,
  userParams: UserParams,
) => {
  const { currentlyInTheaters, sellingTickets, currentlyStreaming } = status
  const { sneakPeek, trailer, movie } = watchableContent
  const { lastWatched } = continueWatchingInfo
  const {
    isGuildMember,
    isGuildMemberContent,
    guildPermissions,
    watchableReasons,
    guildCodes,
    areGuildTicketsAvailable,
    activeSweepstake,
  } = userParams

  // Check if movie can be streamed
  const isStreamable = currentlyStreaming || Boolean(movie?.guid && watchableReasons?.length)

  if (isStreamable) {
    return handleStreamingMovie({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      lastWatched,
      movie,
      isGuildMemberContent,
      isGuildMember,
      watchableReasons,
    })
  }

  if (currentlyInTheaters) {
    return handleInTheatersMovie({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
      guildPermissions,
      areGuildTicketsAvailable,
      guildCodes,
    })
  }

  if (sellingTickets) {
    return handleSellingTicketsMovie({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
      areGuildTicketsAvailable,
      guildCodes,
    })
  }

  if (activeSweepstake) {
    return handleGiveawayTicketsMovie({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
      areGuildTicketsAvailable,
      guildCodes,
    })
  }

  return getPreviewContentCta({
    isPrimary: true,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    isGuildMember,
  })
}

const handleMovieByStatus = (
  projectData: Project,
  t: TranslateFunction,
  defaultCtas: CtaValues,
  projectStatus: ReturnType<typeof getProjectStatus>,
  watchableContent: ReturnType<typeof getWatchableContent>,
  continueWatchingInfo: ReturnType<typeof getContinueWatchingInfo>,
  userParams: UserParams,
) => {
  const { isMovie, currentlyStreaming } = projectStatus
  const { lastWatched } = continueWatchingInfo

  if (projectData?.projectType === 'special') {
    return handleSpecialContent({
      projectData,
      t,
      defaultCtas,
      sneakPeek: watchableContent.sneakPeek,
      trailer: watchableContent.trailer,
      special: watchableContent.special,
      isGuildMember: userParams.isGuildMember,
      lastWatched: lastWatched,
    })
  }

  if (!isMovie) {
    return handleSeriesContent(
      projectData,
      t,
      defaultCtas,
      watchableContent,
      continueWatchingInfo,
      userParams,
      currentlyStreaming,
    )
  }

  return handleMovieContent(
    projectData,
    t,
    defaultCtas,
    projectStatus,
    watchableContent,
    continueWatchingInfo,
    userParams,
  )
}

export const useWatchProjectCTAs = ({
  t,
  projectData,
  bonusVideos,
  isGuildMember,
  ctaPath,
  ctaText,
  isGuildMemberContent,
  guildPermissions,
  watchableReasons,
  guildCodes,
  areGuildTicketsAvailable,
  activeSweepstake,
  isGeoBlockedProject,
  movieSpecialMedias,
  resumeWatchingData,
  ticketStatus,
}: {
  t: TranslateFunction
  projectData: Project
  bonusVideos?: VideoMetaData[]
  isGuildMember: boolean
  ctaPath?: string
  ctaText?: string
  isGuildMemberContent: boolean
  guildPermissions: GuildPermissions
  watchableReasons?: string[]
  guildCodes?: string[]
  areGuildTicketsAvailable?: boolean
  activeSweepstake?: boolean
  isGeoBlockedProject?: boolean
  movieSpecialMedias: Media[]
  resumeWatchingData?: ResumeWatchingData
  ticketStatus?: TicketsStatusReference | null
}): WatchProjectCTAResult => {
  if (isGeoBlockedProject) {
    return {
      primaryCtaText: t('unavailableForLocation', 'Unavailable For Location'),
      primaryCtaPath: 'disabled',
      primaryCtaIcon: 'geo',
      secondaryCtaPath: null,
      secondaryCtaText: null,
      secondaryCtaIcon: null,
      trailer: null,
      primaryCtaGuid: null,
    }
  }

  const watchableContent = getWatchableContent(projectData, bonusVideos, movieSpecialMedias)
  const projectStatus = getProjectStatus(projectData, watchableContent?.movie, ticketStatus)
  const continueWatchingInfo = getContinueWatchingInfo(projectData, resumeWatchingData)
  const defaultCtas = createDefaultCtas(ctaPath, ctaText)

  const userParams = {
    isGuildMember,
    isGuildMemberContent,
    guildPermissions,
    watchableReasons,
    guildCodes,
    areGuildTicketsAvailable,
    activeSweepstake,
  }

  handleMovieByStatus(projectData, t, defaultCtas, projectStatus, watchableContent, continueWatchingInfo, userParams)

  return formatCtaResult(defaultCtas, watchableContent.trailer, watchableContent)
}

const isSpecialEpisodeType = (episode: Episode): boolean => {
  return (
    episode?.isTrailer ||
    isATeaser(episode) ||
    isASneakPeek(episode) ||
    isATorch(episode) ||
    isAPitch(episode) ||
    isALivestream(episode) ||
    isAPreview(episode) ||
    isAClip(episode)
  )
}

const findTrailer = (projectData: Project, bonusVideos?: VideoMetaData[], movieSpecialMedias?: Media[]) => {
  const isTrailerLikeTitle = (raw?: string | null) => {
    const title = (raw ?? '').toLowerCase()
    return title.includes('trailer') || title.includes('teaser')
  }

  // Prefer movie/special medias first (movies/specials)
  if (movieSpecialMedias && movieSpecialMedias.length > 0) {
    const mediaTrailer = movieSpecialMedias.find(
      (video) => video.watchableType === 'TRAILER' || isTrailerLikeTitle(video.title),
    )
    if (mediaTrailer) return { ...mediaTrailer, isBonus: true }
  }

  // Fallback to legacy seasons (series)
  const preferredEpisodes = getFirstSeasonEpisodesPreferred(projectData)
  if (preferredEpisodes && preferredEpisodes.length > 0) {
    const episodeTrailer = preferredEpisodes.find(
      (episode) => Boolean(episode?.isTrailer) || isATeaser(episode) || isTrailerLikeTitle(episode?.name ?? ''),
    )
    if (episodeTrailer) return { ...episodeTrailer, isBonus: false }
  }

  // Finally, look in bonus videos
  if (bonusVideos && bonusVideos.length > 0) {
    const bonusNamedTrailer = bonusVideos.find((video) => isTrailerLikeTitle(video.title))
    if (bonusNamedTrailer) return { ...bonusNamedTrailer, isBonus: true }
  }

  return null
}

const findSneakPeek = (projectData: Project, bonusVideos?: VideoMetaData[], movieSpecialMedias?: Media[]) => {
  const isSneakPeekLikeTitle = (raw?: string | null) => {
    const title = (raw ?? '').toLowerCase()
    return title.includes('sneak peek') || title.includes('sneakpeek')
  }

  // Prefer movie/special medias first
  if (movieSpecialMedias && movieSpecialMedias.length > 0) {
    const mediaSneakPeek = movieSpecialMedias.find(
      (video) => video.watchableType === 'SNEAK_PEEK' || isSneakPeekLikeTitle(video.title),
    )
    if (mediaSneakPeek) return { ...mediaSneakPeek, isBonus: true }
  }

  // Fallback to legacy seasons (series)
  const preferredEpisodes = getFirstSeasonEpisodesPreferred(projectData)
  if (preferredEpisodes && preferredEpisodes.length > 0) {
    const namedSneakPeek = preferredEpisodes.find((episode) => isASneakPeek(episode))
    if (namedSneakPeek) return { ...namedSneakPeek, isBonus: false }
  }

  // Finally, bonus videos
  if (bonusVideos && bonusVideos.length > 0) {
    const bonusNamedSneakPeek = bonusVideos.find((video) => isSneakPeekLikeTitle(video.title))
    if (bonusNamedSneakPeek) return { ...bonusNamedSneakPeek, isBonus: true }
  }

  return null
}

const getRegularBonusVideos = (bonusVideos: VideoMetaData[]): VideoMetaData[] => {
  return bonusVideos.filter((video) => {
    const lowerTitle = video.title?.toLowerCase() || ''
    return !(lowerTitle.includes('trailer') || lowerTitle.includes('teaser') || lowerTitle.includes('sneak peek'))
  })
}

const findFirstWatchableEpisode = (projectData: Project, bonusVideos?: VideoMetaData[]) => {
  const episodes = getFirstSeasonEpisodesPreferred(projectData)
  if (!episodes || episodes.length === 0) return null

  // Filter out special episode types
  const watchableEpisodes = episodes.filter((episode) => !isSpecialEpisodeType(episode) && Boolean(episode?.guid))

  // Sort episodes by number
  const sortedEpisodes = [...watchableEpisodes].sort((a, b) => {
    if (a.episodeNumber && b.episodeNumber) {
      return a.episodeNumber - b.episodeNumber
    }
    return 0
  })

  // Check for watched episodes to find next unwatched, or default to first episode
  if (sortedEpisodes.length > 0) {
    const nextEpisode = findNextEpisode(sortedEpisodes)
    if (nextEpisode) return { ...nextEpisode, isBonus: false }
  }

  // Default to first episode if no next episode found
  if (sortedEpisodes.length > 0) {
    const firstEpisode = sortedEpisodes[0]
    if (firstEpisode) {
      return { ...firstEpisode, isBonus: false }
    }
  } else if (projectData?.slug === slugs.theGermanKing) {
    return episodes[0]
  }

  return findFallbackBonusVideo(bonusVideos)
}

const findNextEpisode = (sortedEpisodes: Episode[]) => {
  // Find episodes with watch position
  const watchedEpisodes = sortedEpisodes.filter(
    (episode) => episode?.watchPosition?.position && episode.watchPosition.position > 0,
  )

  if (watchedEpisodes.length === 0) {
    return null
  }

  // Get latest watched episode by episode number
  const latestWatchedEpisode = getLatestWatchedEpisode(watchedEpisodes)

  if (!latestWatchedEpisode) {
    return null
  }

  return findNextAvailableEpisode(sortedEpisodes, latestWatchedEpisode)
}

const getLatestWatchedEpisode = (watchedEpisodes: Episode[]): Episode | null => {
  if (watchedEpisodes.length === 0) return null
  return (
    [...watchedEpisodes].sort((a, b) => {
      if (a.episodeNumber && b.episodeNumber) {
        return b.episodeNumber - a.episodeNumber
      }
      return 0
    })[0] || null
  )
}

const findNextAvailableEpisode = (sortedEpisodes: Episode[], latestWatchedEpisode: Episode): Episode | null => {
  for (const episode of sortedEpisodes) {
    if (
      episode.episodeNumber &&
      latestWatchedEpisode.episodeNumber &&
      episode.episodeNumber > latestWatchedEpisode.episodeNumber
    ) {
      if (episode?.earlyAccessDate && new Date(episode.earlyAccessDate).getTime() < Date.now()) {
        return episode
      }
    }
  }
  return null
}

const findFallbackBonusVideo = (bonusVideos?: VideoMetaData[]) => {
  if (bonusVideos && bonusVideos.length > 0) {
    const regularBonusVideo = getRegularBonusVideos(bonusVideos)[0]
    if (regularBonusVideo) {
      return { ...regularBonusVideo, isBonus: true }
    }
  }
  return null
}

export const findMovieEpisode = (projectData: Project, movieSpecialMedias?: Media[]): (Episode | Media) | undefined => {
  // Prefer movie/special medias: choose the first non-trailer/teaser/sneak peek item
  if (movieSpecialMedias && movieSpecialMedias.length > 0) {
    const mainMedia = movieSpecialMedias.find((m) => {
      const title = (m.title ?? '').toLowerCase()
      return (
        m.guid &&
        m.watchableType === 'MOVIE' &&
        !title.includes('trailer') &&
        !title.includes('teaser') &&
        !title.includes('sneak peek') &&
        !title.includes('sneakpeek')
      )
    })
    if (mainMedia) return mainMedia
  }

  // Fallback to legacy episodes
  const episodes = projectData?.seasons?.[0]?.episodes
  if (!episodes || episodes.length === 0) return undefined

  const lowerKeywords = ['movie', 'film']

  const foundByName = episodes.find(
    (episode) => !!episode?.name && lowerKeywords.some((keyword) => episode.name!.toLowerCase().includes(keyword)),
  )
  if (foundByName) return foundByName

  const foundBySlug = episodes.find((episode) => {
    const ep = (episode as unknown as { slug?: string }) || {}
    const slug = ep.slug?.toLowerCase()
    return !!slug && lowerKeywords.some((keyword) => slug.includes(keyword))
  })

  return foundBySlug ?? undefined
}

type getPreviewContentCtaParams = {
  isPrimary: boolean
  projectData: Project
  t: TranslateFunction
  defaultCtas: {
    primaryCtaPath: string
    primaryCtaText: string
    primaryCtaIcon: string
    secondaryCtaPath: string
    secondaryCtaText: string
    secondaryCtaIcon: string
  }
  sneakPeek?: ((VideoMetaData | Episode | Media) & { isBonus?: boolean }) | null
  trailer?: ((VideoMetaData | Episode | Media) & { isBonus?: boolean }) | null
  defaultTrailer?: boolean
  isGuildMember?: boolean
}

const getPreviewContentCta = ({
  isPrimary,
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  defaultTrailer = false,
  isGuildMember = false,
}: getPreviewContentCtaParams) => {
  const content = defaultTrailer ? trailer : sneakPeek || trailer

  if (!content) return defaultCtas

  const contentPath = content.isBonus
    ? `/watch/${projectData.slug}/videos/bonus/${content.guid}`
    : `/watch/${projectData.slug}/episode/${content.guid}`

  let contentText

  if (defaultTrailer && trailer && content === trailer) {
    contentText = t('watchTrailer', 'Watch Trailer')
  } else if (!defaultTrailer && sneakPeek && content === sneakPeek && !isGuildMember && !isPrimary) {
    contentText = t('watchSneakPeek', 'Watch Sneak Peek')
  } else {
    if (!isPrimary && isGuildMember && content === sneakPeek) {
      return defaultCtas
    }
    contentText = content === trailer ? t('watchTrailer', 'Watch Trailer') : t('watchSneakPeek', 'Watch Sneak Peek')
  }

  const contentIcon = 'play'

  if (isPrimary) {
    defaultCtas.primaryCtaPath = contentPath
    defaultCtas.primaryCtaText = contentText
    defaultCtas.primaryCtaIcon = contentIcon
  } else {
    defaultCtas.secondaryCtaPath = contentPath
    defaultCtas.secondaryCtaText = contentText
    defaultCtas.secondaryCtaIcon = contentIcon
  }

  return defaultCtas
}

const hasAnyEpisodeAvailable = (projectData: Project): boolean => {
  if (projectData?.projectType !== 'series') return false
  const episodes = getAllEpisodesPreferred(projectData)
  if (!episodes.length) return false
  const now = new Date()
  return episodes.some(
    (episode) =>
      (episode.earlyAccessDate && new Date(episode.earlyAccessDate) < now) ||
      (episode.publiclyAvailableDate && new Date(episode.publiclyAvailableDate) < now),
  )
}

const handleSpecialContent = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  special,
  isGuildMember,
  lastWatched,
}: BaseCtaParams & {
  special?: Episode | Media
  isGuildMember: boolean
  lastWatched?: string
}): CtaValues => {
  const hasSpecial = Boolean(special?.guid)

  if (hasSpecial) {
    const slug = projectData.slug
    const ctaPath = lastWatched ? `/watch/${slug}/episode/${lastWatched}` : `/watch/${slug}/episode/${special!.guid}`

    defaultCtas.primaryCtaText = lastWatched ? t('resume', 'Resume') : t('watchNow', 'Watch Now')
    defaultCtas.primaryCtaPath = ctaPath
    defaultCtas.primaryCtaIcon = 'play'
  }

  getPreviewContentCta({
    isPrimary: !hasSpecial,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    defaultTrailer: true,
    isGuildMember,
  })

  return defaultCtas
}

export const findSpecialEpisode = (
  projectData: Project,
  movieSpecialMedias?: Media[],
): (Episode | Media) | undefined => {
  // Prefer movie/special medias: choose the first non-trailer/teaser/sneak peek item
  if (movieSpecialMedias && movieSpecialMedias.length > 0) {
    const specialMedia = movieSpecialMedias.find((m) => {
      const title = (m.title ?? '').toLowerCase()
      return (
        m.guid &&
        !title.includes('trailer') &&
        !title.includes('teaser') &&
        !title.includes('sneak peek') &&
        !title.includes('sneakpeek')
      )
    })
    if (specialMedia) return specialMedia
  }

  // Fallback to legacy episodes
  const episodes = getFirstSeasonEpisodesPreferred(projectData)
  if (!episodes || episodes.length === 0) return undefined

  const special = episodes.find((episode) => !!episode?.name && episode.name.toLowerCase().includes('special'))

  return special ?? undefined
}
